/**
 * SurfaceFeaturedColumn — the right column's default occupant.
 *
 * Two cards at the same footprint, which is the anchor's point about
 * activations: a playable ad or a live leaderboard takes a POSITION here and
 * wears this chrome, rather than getting a region of its own. That only works
 * if the default occupants are ordinary cards, so they are.
 *
 * Mountable by any surface with something to feature. It takes cards, not a
 * Drop — what is worth featuring is the caller's decision, and on an esports
 * surface it might be a match rather than a creator.
 */

import React from "react";
import {SurfaceMediaCard, type SurfaceMediaCardProps} from "./SurfaceMediaCard";

export interface SurfaceFeaturedColumnProps {
    /**
     * In order. The column is a stack, so the first card is the one a viewer
     * sees without scrolling — the caller decides what earns that, because
     * "most live right now" is a question about the surface, not the column.
     */
    cards: readonly (SurfaceMediaCardProps & { id: string })[];
}

export const SurfaceFeaturedColumn: React.FC<SurfaceFeaturedColumnProps> = ({cards}) => {
    /* Nothing to feature is no column, not an empty one. */
    if (cards.length === 0) return null;

    /*
     * ONE RULE, BOTH POSITIONS. These cards render in the narrow column on some
     * surfaces and in the wide band row on others, and a fragment left the
     * layout to whichever parent caught them — fine at two cards, wrong the
     * moment a Drop authors four videos and they stacked into a tower beside a
     * streak card. A wrapping flex with a real basis stacks in a 400px column
     * and spreads along a full-width row without either being special-cased.
     */
    return (
        <div className="sh-fcards">
            {cards.map(({id, ...card}) => <SurfaceMediaCard key={id} {...card} />)}
        </div>
    );
};

export default SurfaceFeaturedColumn;
